import { PolymerElement, html } from 'polymer';

/**
 * @customElement
 * @polymer
 * @appliesMixin HIP.BehaviorInteractiveElement
 * @appliesMixin HIP.BehaviorA11yKeyboard
 * @appliesMixin HIP.BehaviorSetFocus
 * @appliesMixin HIP.BehaviorElement
 */
class ResultGroupElement extends HIP.BehaviorSetFocus(
  HIP.BehaviorBrowserEvents(
    HIP.BehaviorA11yKeyboard(HIP.BehaviorInteractiveElement(HIP.BehaviorElement(PolymerElement)))
  )
) {
  static get is() {
    return 'hi-result-group';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <style>
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
          border: 0;
        }

        #warningMessage,
        #infoMessage {
          font-size: var(--hi-font-size--small-x);
          line-height: var(--hi-font-lh--small-x);
          color: var(--hi-color--text);
        }

        #warningMessage {
          background: var(--hi-color--yellow-20);
          padding: var(--hi-size--small-x);
        }

        #infoMessage {
          background: var(--hi-color--gray-20);
        }

        #infoMessage ::slotted(*) {
          padding: var(--hi-size--small-x);
          margin: 0;
        }
      </style>
      <p id="warningMessage" role="alert"></p>
      <div id="container">
        <slot id="items"></slot>
      </div>
      <div id="infoMessage">
        <slot name="infoMessage"></slot>
      </div>
    `;
  }

  static get properties() {
    return {
      filterRule: {
        type: String,
        observer: '__filterRuleChanged'
      },
      filterRegExp: {
        type: Object,
        computed: '__filterRegExpCompute(filterRule)'
      },
      /**
       * fewest results permitted before a warning message will show
       */
      min: {
        type: Number,
        value: 0
      },
      /**
       * highest number of results permitted before a warning message will show
       */
      max: {
        type: Number,
        value: 20
      },
      /**
       * override the default message if the results are less than the `min`
       */
      labelMin: String,
      /**
       * override the default message if the results are less than the `max`
       */
      labelMax: String,
      /**
       * a warning message that will show, if the results are higher than `min` and lower than `max`
       */
      labelWarning: String,
      /**
       * a message which overrides all others
       */
      labelOverride: String
    };
  }

  static get observers() {
    return ['__minMaxResultObserver(min, max)'];
  }

  static get api() {
    return ['shiftFocusDown', 'shiftFocusUp', 'focusIndex', 'focusItem', 'selectIndex', 'selectItem'];
  }

  /**
   * @see chosen-ended
   * @event space-keyup-ended
   * @param {Number} index the index of the hi-item selected (starts at 0)
   * @param {ItemElement} item the hi-item selected
   */

  /**
   * @see chosen-ended
   * @event enter-keyup-ended
   * @param {Number} index the index of the hi-item selected (starts at 0)
   * @param {ItemElement} item the hi-item selected
   */

  /**
   * @event right-keyup-ended
   */

  /**
   * @event left-keyup-ended
   */

  /**
   * @event up-keyup-ended
   */

  /**
   * @event down-keyup-ended
   */

  static get keyBindings() {
    return {
      'space:keydown': '_onKeyAny__preventDefault',
      'space:keyup': '_setFocus__onKeyUp__space',
      'enter:keyup': '_setFocus__onKeyUp__enter',
      'up:keydown': '_onKeyAny__preventDefault',
      'down:keydown': '_onKeyAny__preventDefault',
      'right:keyup': '_setFocus__onKeyUp_right',
      'down:keyup': '_setFocus__onKeyUp_down',
      'left:keyup': '_setFocus__onKeyUp_left',
      'up:keyup': '_setFocus__onKeyUp_up'
    };
  }

  static get browserBindings() {
    return {
      focus: '_setFocus__onFocus'
    };
  }

  static get valueEvent() {
    return 'chosen-ended';
  }

  static get autoBinding() {
    return {
      signal: 'focused-index-changed',
      slot: 'focusedIndex'
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'listbox');
    this.valueAttr = 'value';
  }

  __onSlotChanged() {
    this._setFocus__onSlotChanged();
    this.__setMessageOnChildrenSize();

    this.__filterRuleChanged(this.filterRule);
  }

  __minMaxResultObserver() {
    this.__setMessageOnChildrenSize();
  }

  __setMessageOnChildrenSize() {
    const childrenSize = this.slotChildren.length;
    const $warningMessage = this.$.warningMessage;
    const labelWarning = this.labelWarning;
    const labelOverride = this.labelOverride;

    $warningMessage.style.display = 'block';

    if (labelOverride && labelOverride.length > 0) {
      $warningMessage.textContent = labelOverride;
    } else if (childrenSize <= this.min) {
      $warningMessage.textContent = this.labelMin || `${childrenSize} results found`;
    } else if (childrenSize > this.max) {
      $warningMessage.textContent =
        this.labelMax || `Your search has more than ${this.max} results, refine your search for better results.`;
    } else if (labelWarning && labelWarning.length > 0) {
      $warningMessage.textContent = labelWarning;
    } else {
      $warningMessage.textContent = '';
      $warningMessage.style.display = 'none';
    }
  }

  __filterRegExpCompute(_filterRule) {
    return ResultGroupElement.createFilterRegExp(_filterRule);
  }

  /**
   *
   */
  static createFilterRegExp(_filterRule) {
    return new RegExp(`(${_filterRule.replace(/[^a-z0-9\s\-_]/gi, '')})`, 'ig');
  }

  __filterRuleChanged(_filterRule) {
    if (!_filterRule) {
      return;
    }

    const filterRegExp = this.filterRegExp;

    this.slotChildren.forEach((child) => {
      child.filterRegExp = filterRegExp;
    });

    this.hiddenChildren.forEach((node) => {
      node.filterRule = filterRegExp;

      if (node.render) {
        node.render();
      }
    });

    this.__setMessageOnChildrenSize();
  }
}

HIP.factory.element(ResultGroupElement);
